import { useMemo } from 'react';
import { LazyJsonViewer } from '@components/json/LazyJsonViewer';
import type { JsonViewerKeyRenderer, Path } from '@lio/json-viewer';
import { useStore } from '@store/GlobalStore';
import { useStructuredQuote } from '@services/quoteDetails';
import { Box, Theme } from '@mui/material';
import { validateAvailabilityByBooleanOrRoles } from '@pages/QuoteDetails/helpers';
import { getEngine } from '@lio/rule-engine';
import { shallow } from 'zustand/shallow';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import cuid from 'cuid';

interface AttributesTabContentProps {
  quoteId: string;
  isDialogMode?: boolean;
  searchValue?: string;
  attributeType?: 'all' | 'active';
}

export const AttributesTabContent = ({
  quoteId,
  isDialogMode = false,
  searchValue = '',
  attributeType = 'all',
}: AttributesTabContentProps) => {
  const { structuredQuote } = useStructuredQuote(quoteId);
  const roles = useStore((state) => state.roles, shallow);

  const validSearchValue = !!searchValue;

  const filteredAttributes = useMemo(() => {
    if (!structuredQuote) return {};

    const ruleEngine = getEngine(structuredQuote);
    const attributes: Record<string, any> = {};

    structuredQuote.quoteItems.forEach((qi) => {
      ruleEngine.setEngineInitialQuoteItem(qi.id);
      const { quoteItemType, instanceNumber, id, policyLifetimeId, parentId, attributeTypes } = qi;
      let idx = `${quoteItemType.codeName}${instanceNumber ? `-${instanceNumber}` : ''}`;

      if (attributes[idx]) {
        idx = idx + ` #${cuid()?.substring(0, 5)}`;
      }
      const attTypes: Record<string, any> = {};

      Object.entries(attributeTypes).forEach(([att, attType]) => {
        const cleanAttType: Record<string, any> = { ...attType };
        Object.keys(cleanAttType).forEach((propKey) => {
          if (cleanAttType[propKey] == null || propKey === 'properties') delete cleanAttType[propKey];
        });

        let isActive = false;
        try {
          isActive = validateAvailabilityByBooleanOrRoles(
            ruleEngine.execute(cleanAttType.isActive) as boolean | string[],
            roles,
          );
        } catch {
          isActive = false;
        }

        if (attributeType === 'all' || (attributeType === 'active' && isActive)) {
          if (!validSearchValue || cleanAttType.codeName?.toLowerCase().includes(searchValue.toLowerCase())) {
            attTypes[att] = cleanAttType;
          }
        }
      });

      if (
        Object.keys(attTypes).length > 0 ||
        (validSearchValue && idx.toLowerCase().includes(searchValue.toLowerCase()))
      ) {
        attributes[idx] = {
          id,
          instanceNumber,
          policyLifetimeId,
          parentId,
          quoteItemType: {
            id: quoteItemType.id,
            codeName: quoteItemType.codeName,
            isAutoCreated: quoteItemType.isAutoCreated,
            isActive: quoteItemType.isActive,
            quoteItemTypeRules: quoteItemType.quoteItemTypeRules,
          },
          attributeTypes: attTypes,
        };
      }
    });

    return attributes;
  }, [structuredQuote, roles, attributeType, searchValue, validSearchValue]);

  const KeyRenderer: JsonViewerKeyRenderer = ({ path }: { path: Path }) => {
    const value = path.at(-1);
    const highlight =
      validSearchValue && typeof value === 'string' && value.toLowerCase().includes(searchValue.toLowerCase());

    if (!highlight) return value;

    const parts = value.split(new RegExp(`(${searchValue})`, 'gi'));
    return (
      <Box component='span'>
        {parts.map((part, i) => (
          <Box
            key={i}
            component='span'
            sx={part.toLowerCase() === searchValue.toLowerCase() ? { background: 'yellow' } : {}}
          >
            {part}
          </Box>
        ))}
      </Box>
    );
  };
  KeyRenderer.when = () => true;

  const numberOfItems = Object.keys(filteredAttributes).length;
  return (
    <Box data-testid='attributes-tab-content' sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          ...(isDialogMode && { paddingBottom: 0 }),
        }}
      >
        <LazyJsonViewer
          value={filteredAttributes}
          defaultInspectDepth={numberOfItems > 30 ? 1 : numberOfItems > 20 ? 2 : 3}
          objectSortKeys
          sx={{
            overflowY: 'auto',
            fontSize: ({ palette }: Theme) => `${adjustFontSize(11, palette.fontSize)}px`,
            fontWeight: 300,
            backgroundColor: 'white',
          }}
          keyRenderer={KeyRenderer}
        />
      </Box>
    </Box>
  );
};
